import { TrendingUp, Activity, AlertCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

export default function ControlCentrePerformance() {
  // Mock performance data
  const lighthouseData = [
    { date: "Nov 15", performance: 92, accessibility: 98, bestPractices: 95, seo: 100 },
    { date: "Nov 16", performance: 94, accessibility: 98, bestPractices: 96, seo: 100 },
    { date: "Nov 17", performance: 91, accessibility: 97, bestPractices: 95, seo: 100 },
    { date: "Nov 18", performance: 95, accessibility: 98, bestPractices: 97, seo: 100 },
    { date: "Nov 19", performance: 93, accessibility: 98, bestPractices: 96, seo: 100 },
    { date: "Nov 20", performance: 96, accessibility: 99, bestPractices: 97, seo: 100 },
    { date: "Nov 21", performance: 94, accessibility: 98, bestPractices: 96, seo: 100 },
  ];

  const gscData = [
    { date: "Nov 15", impressions: 1240, clicks: 45, ctr: 3.6, position: 8.2 },
    { date: "Nov 16", impressions: 1350, clicks: 52, ctr: 3.9, position: 7.8 },
    { date: "Nov 17", impressions: 1180, clicks: 41, ctr: 3.5, position: 8.5 },
    { date: "Nov 18", impressions: 1520, clicks: 58, ctr: 3.8, position: 7.5 },
    { date: "Nov 19", impressions: 1430, clicks: 54, ctr: 3.8, position: 7.7 },
    { date: "Nov 20", impressions: 1680, clicks: 67, ctr: 4.0, position: 7.2 },
    { date: "Nov 21", impressions: 1590, clicks: 61, ctr: 3.8, position: 7.4 },
  ];

  return (
    <div className="flex h-screen w-full flex-col">
      <PageHeader
        title="Performance Monitoring"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Project Manager" }, { label: "Performance" }]}
        actions={
          <Button variant="outline" data-testid="button-configure-metrics">
            Configure Metrics
          </Button>
        }
      />

      <main className="flex-1 overflow-auto bg-muted/20 p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="card-lighthouse-performance">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Lighthouse Performance</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">94</div>
                <p className="text-xs text-muted-foreground">
                  <span className="text-green-600">↑ 2</span> from last week
                </p>
              </CardContent>
            </Card>

            <Card data-testid="card-vercel-insights">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Real User Metrics</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">342ms</div>
                <p className="text-xs text-muted-foreground">
                  Avg page load time
                </p>
              </CardContent>
            </Card>

            <Card data-testid="card-sentry-errors">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">0.02%</div>
                <p className="text-xs text-muted-foreground">
                  <span className="text-green-600">↓ 0.01%</span> from last week
                </p>
              </CardContent>
            </Card>

            <Card data-testid="card-gsc-clicks">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Search Clicks</CardTitle>
                <Search className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">378</div>
                <p className="text-xs text-muted-foreground">
                  Last 7 days
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Lighthouse Trends */}
          <Card data-testid="card-lighthouse-chart">
            <CardHeader>
              <CardTitle>Lighthouse Core Web Vitals</CardTitle>
              <CardDescription>Performance scores over the last 7 days</CardDescription>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={lighthouseData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "0.5rem",
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="performance" stroke="hsl(var(--chart-1))" name="Performance" strokeWidth={2} />
                  <Line type="monotone" dataKey="accessibility" stroke="hsl(var(--chart-2))" name="Accessibility" strokeWidth={2} />
                  <Line type="monotone" dataKey="bestPractices" stroke="hsl(var(--chart-3))" name="Best Practices" strokeWidth={2} />
                  <Line type="monotone" dataKey="seo" stroke="hsl(var(--chart-4))" name="SEO" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Google Search Console */}
          <Card data-testid="card-gsc-chart">
            <CardHeader>
              <CardTitle>Google Search Console</CardTitle>
              <CardDescription>Search performance metrics over the last 7 days</CardDescription>
            </CardHeader>
            <CardContent className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={gscData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="date" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "0.5rem",
                    }}
                  />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="hsl(var(--chart-1))" name="Impressions" strokeWidth={2} />
                  <Line yAxisId="left" type="monotone" dataKey="clicks" stroke="hsl(var(--chart-2))" name="Clicks" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="ctr" stroke="hsl(var(--chart-3))" name="CTR (%)" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
